import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { UserGroupIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { Button, Input, Modal } from '../ui';
import type { Client, FamilyMember } from '../../types';
import {
  applyDominicanIDMask,
  calculateAge,
  cleanDominicanID,
  getMaxDateOfBirth,
  getMinDateOfBirth
} from '../../utils/validation';

export interface FamilyMemberFormData {
  name?: string;
  lastname?: string;
  cedula?: string;
  relationship: string;
  age?: number;
  dateOfBirth?: string;
  observacion?: string;
  hasSeparateFamily?: boolean;
  clientId: string;
}

/** Valores internos del formulario (incluye detalle si relación = otro). */
type FormValues = FamilyMemberFormData & {
  relationshipDetail?: string;
};

const RELATIONSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: 'esposo', label: 'Cónyuge' },
  { value: 'hijo', label: 'Hijo(a)' },
  { value: 'padre', label: 'Padre' },
  { value: 'madre', label: 'Madre' },
  { value: 'hermano', label: 'Hermano(a)' },
  { value: 'abuelo', label: 'Abuelo(a)' },
  { value: 'nieto', label: 'Nieto(a)' },
  { value: 'tio', label: 'Tío(a)' },
  { value: 'primo', label: 'Primo(a)' },
  { value: 'suegro', label: 'Suegro(a)' },
  { value: 'yerno', label: 'Yerno / nuera' },
  { value: 'cuñado', label: 'Cuñado(a)' },
  { value: 'otro', label: 'Otra relación' }
];

function SectionTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <h3 className={clsx('text-xs font-bold uppercase tracking-wider text-slate-500', className)}>{children}</h3>
  );
}

function mergeObservacionWithOtro(base: string | undefined, detail: string | undefined): string | undefined {
  const d = detail?.trim();
  if (!d) return base?.trim() || undefined;
  const line = `Relación indicada: ${d}`;
  const b = base?.trim() || '';
  if (b.includes(line)) return b || undefined;
  return b ? `${line}\n${b}` : line;
}

export const FamilyMemberModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  familyMember?: FamilyMember | null;
  clientId: string;
  client?: Client | null;
  onSave: (data: FamilyMemberFormData) => void;
  isLoading?: boolean;
}> = ({ isOpen, onClose, familyMember, clientId, client, onSave, isLoading = false }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
    setFocus
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      lastname: '',
      cedula: '',
      relationship: '',
      relationshipDetail: '',
      age: 0,
      dateOfBirth: '',
      observacion: '',
      hasSeparateFamily: false,
      clientId
    }
  });

  const watchedDateOfBirth = watch('dateOfBirth');
  const relationship = watch('relationship');

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => setFocus('name'), 50);
    return () => window.clearTimeout(t);
  }, [isOpen, setFocus]);

  useEffect(() => {
    if (familyMember) {
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().split('T')[0];
      };

      let observacion = familyMember.observacion || '';
      let relationshipDetail = '';
      if (familyMember.relationship === 'otro' && observacion.startsWith('Relación indicada:')) {
        const nl = observacion.indexOf('\n');
        const first = nl === -1 ? observacion : observacion.slice(0, nl);
        const rest = nl === -1 ? '' : observacion.slice(nl + 1).trim();
        const prefix = 'Relación indicada: ';
        if (first.startsWith(prefix)) {
          relationshipDetail = first.slice(prefix.length).trim();
          observacion = rest;
        }
      }

      reset({
        name: familyMember.name || '',
        lastname: familyMember.lastname || '',
        cedula: applyDominicanIDMask(familyMember.cedula || ''),
        relationship: familyMember.relationship,
        relationshipDetail,
        age: familyMember.age || 0,
        dateOfBirth: formatDateForInput(familyMember.dateOfBirth || ''),
        observacion,
        hasSeparateFamily: familyMember.hasSeparateFamily || false,
        clientId
      });
    } else {
      reset({
        name: '',
        lastname: '',
        cedula: '',
        relationship: '',
        relationshipDetail: '',
        age: 0,
        dateOfBirth: '',
        observacion: '',
        hasSeparateFamily: false,
        clientId
      });
    }
  }, [familyMember, clientId, reset, isOpen]);

  const submit = (data: FormValues) => {
    const { relationshipDetail, ...rest } = data;
    const observacion = mergeObservacionWithOtro(rest.observacion, relationship === 'otro' ? relationshipDetail : undefined);
    onSave({
      ...rest,
      observacion
    });
  };

  const handleClose = () => {
    if (isLoading) return;
    reset({
      name: '',
      lastname: '',
      cedula: '',
      relationship: '',
      relationshipDetail: '',
      age: 0,
      dateOfBirth: '',
      observacion: '',
      hasSeparateFamily: false,
      clientId
    });
    onClose();
  };

  const cedulaDupMessage = (value: string | undefined) => {
    if (!value?.trim()) return true;
    const mine = cleanDominicanID(value);
    if (mine.length !== 11) return true;
    if (!client?.familyMembers) return true;
    const dup = client.familyMembers.some((fm) => {
      if (fm.id === familyMember?.id) return false;
      const other = fm.cedula ? cleanDominicanID(fm.cedula) : '';
      return other === mine;
    });
    return dup ? 'Ya existe un familiar con esta cédula' : true;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div>
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white shadow-sm">
              <UserGroupIcon className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold leading-tight text-slate-900 sm:text-xl">
                {familyMember ? 'Editar familiar' : 'Agregar familiar'}
              </h2>
              <p className="mt-1.5 text-sm leading-snug text-slate-500">
                Registre a la persona vinculada al titular del plan. Los campos obligatorios están marcados con{' '}
                <span className="text-rose-600">*</span>.
              </p>
            </div>
          </div>
        </div>
      }
      size="lg"
    >
      <form onSubmit={handleSubmit(submit)} className="space-y-8">
        <div className="space-y-6">
          <div>
            <SectionTitle>Identificación</SectionTitle>
            <p className="mt-1 text-sm text-slate-600">Nombre y documento ayudan a evitar duplicados en el padrón.</p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Nombre (opcional)"
                placeholder="Ej. María"
                error={errors.name?.message}
                disabled={isLoading}
                {...register('name')}
              />
              <Input
                label="Apellido (opcional)"
                placeholder="Ej. Pérez"
                error={errors.lastname?.message}
                disabled={isLoading}
                {...register('lastname')}
              />
            </div>
            <div className="mt-4 max-w-md">
              <Input
                label="Cédula (opcional)"
                placeholder="000-0000000-0"
                error={errors.cedula?.message}
                disabled={isLoading}
                {...register('cedula', {
                  validate: async (value) => {
                    if (!value || value.trim() === '') return true;
                    const cleanValue = cleanDominicanID(value);
                    if (cleanValue.length !== 11) return 'La cédula debe tener 11 dígitos';
                    if (!/^\d{11}$/.test(cleanValue)) return 'Solo números, sin letras';
                    return cedulaDupMessage(value);
                  }
                })}
                onChange={(value) => {
                  setValue('cedula', applyDominicanIDMask(value), { shouldValidate: true });
                }}
              />
              <p className="mt-1.5 text-xs text-slate-500">Si la completa, debe ser válida y única entre familiares.</p>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <SectionTitle>Vínculo con el titular</SectionTitle>
            <p className="mt-1 text-sm text-slate-600">Indique cómo se relaciona esta persona con el cliente titular.</p>
            <div className="mt-4 max-w-xl">
              <label htmlFor="family-relationship" className="mb-2 block text-sm font-bold text-slate-800">
                Parentesco <span className="text-rose-600">*</span>
              </label>
              <select
                id="family-relationship"
                {...register('relationship', { required: 'Seleccione un parentesco' })}
                disabled={isLoading}
                className={clsx(
                  'block w-full rounded-xl border px-4 py-3 text-sm font-medium text-slate-900 shadow-sm transition',
                  errors.relationship
                    ? 'border-rose-300 ring-2 ring-rose-100'
                    : 'border-slate-300 hover:border-slate-400 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200',
                  'disabled:cursor-not-allowed disabled:bg-slate-100'
                )}
              >
                <option value="">Seleccione una opción</option>
                {RELATIONSHIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {errors.relationship && (
                <p className="mt-2 text-sm font-medium text-rose-700">{errors.relationship.message}</p>
              )}
            </div>

            {relationship === 'otro' && (
              <div className="mt-4 max-w-xl animate-fade-in">
                <Input
                  label="Especifique la relación (opcional)"
                  placeholder="Ej. padrastro, ex cónyuge"
                  error={errors.relationshipDetail?.message}
                  disabled={isLoading}
                  {...register('relationshipDetail', {
                    maxLength: { value: 120, message: 'Máximo 120 caracteres' }
                  })}
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  Se guardará junto a las observaciones para referencia en cobertura y reportes.
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-6">
            <SectionTitle>Fecha de nacimiento</SectionTitle>
            <p className="mt-1 text-sm text-slate-600">No se permiten fechas futuras. La edad se calcula automáticamente.</p>
            <div className="mt-4 max-w-xs">
              <Input
                label="Fecha de nacimiento (opcional)"
                type="date"
                error={errors.dateOfBirth?.message}
                disabled={isLoading}
                {...register('dateOfBirth', {
                  validate: (value) => {
                    if (!value) return true;
                    const d = new Date(value);
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    if (d > today) return 'La fecha no puede ser futura';
                    return true;
                  },
                  max: {
                    value: getMaxDateOfBirth(),
                    message: 'La fecha no puede ser posterior a hoy'
                  },
                  min: {
                    value: getMinDateOfBirth(),
                    message: 'Revise la fecha (límite razonable 120 años)'
                  }
                })}
              />
              {watchedDateOfBirth && (
                <p className="mt-2 inline-flex rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-900 ring-1 ring-emerald-200/80">
                  Edad calculada: {calculateAge(watchedDateOfBirth)} años
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <SectionTitle>Cobertura del plan</SectionTitle>
            <div
              className={clsx(
                'mt-4 rounded-2xl border p-4 sm:p-5',
                watch('hasSeparateFamily')
                  ? 'border-amber-300 bg-amber-50/80 ring-1 ring-amber-200/60'
                  : 'border-slate-200 bg-slate-50/50 ring-1 ring-slate-100'
              )}
            >
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  {...register('hasSeparateFamily')}
                  disabled={isLoading}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                />
                <span className="flex-1">
                  <span className="block text-sm font-bold text-slate-900">Tiene familia aparte</span>
                  <span className="mt-1 block text-sm leading-relaxed text-slate-600">
                    Actívelo si esta persona constituye un núcleo familiar distinto al del titular. En ese caso, la
                    cobertura del plan <strong className="font-semibold text-slate-800">no aplica</strong> a ese núcleo,
                    según las reglas de su funeraria.
                  </span>
                </span>
              </label>
              <div className="mt-3 flex gap-2 rounded-lg bg-white/70 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-100">
                <InformationCircleIcon className="h-4 w-4 shrink-0 text-amber-700" aria-hidden />
                <span>Esta marca es importante para cotizaciones y servicios posteriores.</span>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <SectionTitle>Observaciones</SectionTitle>
            <label htmlFor="family-obs" className="mb-2 block text-sm font-bold text-slate-800">
              Notas internas (opcional)
            </label>
            <textarea
              id="family-obs"
              {...register('observacion', { maxLength: { value: 2000, message: 'Máximo 2000 caracteres' } })}
              disabled={isLoading}
              rows={4}
              placeholder="Aclaraciones médicas, preferencias o contexto útil para el equipo."
              className={clsx(
                'block w-full rounded-xl border px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400',
                'border-slate-300 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200',
                'disabled:cursor-not-allowed disabled:bg-slate-100',
                errors.observacion && 'border-rose-300 ring-2 ring-rose-100'
              )}
            />
            {errors.observacion && (
              <p className="mt-2 text-sm font-medium text-rose-700">{errors.observacion.message}</p>
            )}
            <p className="mt-1.5 text-xs text-slate-500">Visible en el expediente del familiar; no es visible para el cliente final.</p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">
            {isLoading ? 'Guardando cambios…' : 'Revise los datos antes de confirmar.'}
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading} className="min-w-[100px]">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="danger"
              loading={isLoading}
              disabled={isLoading || !isValid}
              className="min-w-[140px] bg-slate-900 hover:bg-slate-800"
            >
              {familyMember ? 'Guardar cambios' : 'Registrar familiar'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
